import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pracaService, Praca } from '../../services/praca';

const PracaTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Buscar todas as praças
  const { data: pracas = [], isLoading, error } = useQuery({
    queryKey: ['pracas'],
    queryFn: pracaService.getAll,
  });

  // Filtrar praças baseado na busca
  const filteredPracas = useMemo(() => {
    return pracas.filter(praca =>
      praca.nomePraca.toLowerCase().includes(search.toLowerCase()) ||
      praca.nomeFuncionario.toLowerCase().includes(search.toLowerCase())
    );
  }, [pracas, search]);

  // Paginação dos dados filtrados
  const paginatedPracas = useMemo(() => {
    return filteredPracas.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredPracas, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (id: number) => {
    navigate(`/pracas/editar/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/pracas/visualizar/${id}`);
  };

  const handleNew = () => {
    navigate('/pracas/novo');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar as praças.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Filtrar"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            },
          }}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
        >
          Nova Praça
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Praça</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Funcionário</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data Vínculo</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPracas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhuma praça encontrada para o filtro informado.' : 'Nenhuma praça cadastrada.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPracas.map((praca) => (
                <TableRow key={praca.id} hover>
                  <TableCell>{praca.id}</TableCell>
                  <TableCell>{praca.nomePraca}</TableCell>
                  <TableCell>{praca.nomeFuncionario}</TableCell>
                  <TableCell>
                    {praca.dataVinculo 
                      ? new Date(praca.dataVinculo).toLocaleDateString('pt-BR')
                      : 'N/A'
                    }
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(praca.id)}
                      size="small"
                      title="Editar"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="info"
                      onClick={() => handleView(praca.id)}
                      size="small"
                      title="Visualizar"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredPracas.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />
    </Paper>
  );
};

export default PracaTable;
